
import React, { useState, useEffect, useRef } from 'react';
import { HistoryItem, AspectRatio } from './types';
import { fileToBase64 } from './utils/fileUtils';
import { editImage, generateVideo } from './services/geminiService';
import AuthGuard from './components/AuthGuard';

const LoadingSpinner: React.FC = () => (
  <div className="flex justify-center items-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400"></div>
  </div>
);


const InitialSetup: React.FC<{ onSetupComplete: (file: File, instructions: string) => void }> = ({ onSetupComplete }) => {
    const [file, setFile] = useState<File | null>(null);
    const [instructions, setInstructions] = useState<string>("Maintain the core fashion item and model pose as closely as possible. The background, lighting conditions, and styling can be altered based on the prompt.");
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
        }
    };

    const handleSubmit = () => {
        if (file && instructions) {
            onSetupComplete(file, instructions);
        }
    };

    return (
        <div className="flex flex-col items-center justify-center h-screen bg-gray-900 p-8">
            <div className="w-full max-w-lg bg-gray-800 p-8 rounded-2xl shadow-xl">
                <h2 className="text-2xl font-bold text-purple-300 mb-6 text-center">Initial Setup</h2>
                
                <div className="mb-6">
                    <label htmlFor="instructions" className="block mb-2 text-sm font-medium text-gray-300">Base Instructions for AI</label>
                    <textarea
                        id="instructions"
                        value={instructions}
                        onChange={(e) => setInstructions(e.target.value)}
                        rows={4}
                        className="bg-gray-700 border border-gray-600 text-gray-200 text-sm rounded-lg focus:ring-purple-500 focus:border-purple-500 block w-full p-2.5"
                        placeholder="e.g., Keep the dress, change the background..."
                    />
                </div>

                <div className="mb-8">
                    <label htmlFor="image-upload" className="block mb-2 text-sm font-medium text-gray-300">Upload Product Image</label>
                    <div 
                        className="flex items-center justify-center w-full h-32 border-2 border-gray-600 border-dashed rounded-lg cursor-pointer bg-gray-700 hover:bg-gray-600"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {file ? (
                            <p className="text-gray-400">{file.name}</p>
                        ) : (
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                <svg className="w-8 h-8 mb-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16"><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/></svg>
                                <p className="mb-2 text-sm text-gray-500"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                            </div>
                        )}
                        <input id="file-upload" type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept="image/*" />
                    </div>
                </div>

                <button
                    onClick={handleSubmit}
                    disabled={!file || !instructions}
                    className="w-full text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-500 focus:ring-4 focus:outline-none focus:ring-purple-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center transition-colors"
                >
                    Start Visualizing
                </button>
            </div>
        </div>
    );
};


export default function App() {
    const [baseInstructions, setBaseInstructions] = useState<string>('');
    const [history, setHistory] = useState<HistoryItem[]>([]);
    const [currentPrompt, setCurrentPrompt] = useState<string>('');
    const [videoPrompt, setVideoPrompt] = useState<string>('');
    const [aspectRatio, setAspectRatio] = useState<AspectRatio>('16:9');

    const [isEditingImage, setIsEditingImage] = useState<boolean>(false);
    const [isVideoGenerating, setIsVideoGenerating] = useState<boolean>(false);
    const [videoGenerationStatus, setVideoGenerationStatus] = useState<string>('');
    const [generatedVideoUrl, setGeneratedVideoUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    
    const [activeImage, setActiveImage] = useState<HistoryItem | null>(null);
    const historyEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (history.length > 0) {
            setActiveImage(history[history.length - 1]);
        } else {
            setActiveImage(null);
        }
    }, [history]);
    
    useEffect(() => {
        historyEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [history]);

    const handleInitialSetup = async (file: File, instructions: string) => {
        const base64Image = await fileToBase64(file);
        const initialItem: HistoryItem = {
            id: crypto.randomUUID(),
            prompt: 'Initial Upload',
            imageUrl: base64Image,
            mimeType: file.type,
        };
        setBaseInstructions(instructions);
        setHistory([initialItem]);
    };

    const handleEditImage = async () => {
        if (!currentPrompt.trim() || history.length === 0) return;

        setIsEditingImage(true);
        setError(null);
        const latestImage = history[history.length - 1];

        try {
            const newImage = await editImage(latestImage, currentPrompt, baseInstructions);
            const newItem: HistoryItem = {
                id: crypto.randomUUID(),
                prompt: currentPrompt,
                imageUrl: newImage.data,
                mimeType: newImage.mimeType,
            };
            setHistory(prev => [...prev, newItem]);
            setCurrentPrompt('');
        } catch (e) {
            console.error(e);
            setError(e instanceof Error ? e.message : "An unknown error occurred during image editing.");
        } finally {
            setIsEditingImage(false);
        }
    };
    
    const handleGenerateVideo = async () => {
        if (!videoPrompt.trim() || !activeImage) return;

        setIsVideoGenerating(true);
        setGeneratedVideoUrl(null);
        setError(null);
        
        try {
            const url = await generateVideo(activeImage, videoPrompt, aspectRatio, setVideoGenerationStatus);
            setGeneratedVideoUrl(url);
        } catch (e) {
            console.error(e);
            const errorMessage = e instanceof Error ? e.message : "An unknown error occurred during video generation.";
            setError(errorMessage);
        } finally {
            setIsVideoGenerating(false);
            setVideoGenerationStatus('');
        }
    };
    
    if (history.length === 0) {
        return (
          <AuthGuard>
            <InitialSetup onSetupComplete={handleInitialSetup} />
          </AuthGuard>
        );
    }

    return (
      <AuthGuard>
        <div className="flex h-screen bg-gray-900 text-gray-200 font-sans">
        {/* Left Panel */}
        <div className="w-1/3 flex flex-col border-r border-gray-700">
          {/* History Panel */}
          <div className="flex-1 p-4 overflow-y-auto">
            <h2 className="text-xl font-bold mb-4 text-purple-300">History</h2>
            <div className="space-y-4">
              {history.map((item) => (
                <div
                  key={item.id}
                  onClick={() => setActiveImage(item)}
                  className={`flex items-center p-2 rounded-lg cursor-pointer transition-all ${activeImage?.id === item.id ? 'bg-purple-900/50 ring-2 ring-purple-500' : 'bg-gray-800 hover:bg-gray-700'}`}
                >
                  <img
                    src={`data:${item.mimeType};base64,${item.imageUrl}`}
                    alt={item.prompt}
                    className="w-16 h-16 object-cover rounded-md mr-4"
                  />
                  <p className="text-sm text-gray-300 flex-1 truncate">{item.prompt}</p>
                </div>
              ))}
              <div ref={historyEndRef} />
            </div>
          </div>
          
          {/* Controls Panel */}
          <div className="p-4 border-t border-gray-700 bg-gray-800/50">
            {/* Image Editing */}
            <div className="mb-6">
                <h3 className="font-semibold text-lg mb-2 text-purple-400">Edit Image</h3>
                <textarea
                    value={currentPrompt}
                    onChange={(e) => setCurrentPrompt(e.target.value)}
                    placeholder="e.g., Add a retro filter..."
                    rows={3}
                    className="bg-gray-700 border border-gray-600 text-gray-200 text-sm rounded-lg focus:ring-purple-500 focus:border-purple-500 block w-full p-2.5 mb-2"
                    disabled={isEditingImage}
                />
                <button
                    onClick={handleEditImage}
                    disabled={isEditingImage || !currentPrompt.trim()}
                    className="w-full text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-500 focus:ring-4 focus:outline-none focus:ring-purple-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center transition-colors"
                >
                    {isEditingImage ? <LoadingSpinner /> : 'Generate Edit'}
                </button>
            </div>
            {/* Video Generation */}
            <div>
                <h3 className="font-semibold text-lg mb-2 text-green-400">Generate Video</h3>
                 <textarea
                    value={videoPrompt}
                    onChange={(e) => setVideoPrompt(e.target.value)}
                    placeholder="e.g., A cinematic pan of the model..."
                    rows={3}
                    className="bg-gray-700 border border-gray-600 text-gray-200 text-sm rounded-lg focus:ring-green-500 focus:border-green-500 block w-full p-2.5 mb-2"
                    disabled={isVideoGenerating}
                />
                <div className="flex gap-4 mb-2">
                    <label className="flex-1">
                        <span className="text-sm">Aspect Ratio:</span>
                        <select
                            value={aspectRatio}
                            onChange={(e) => setAspectRatio(e.target.value as AspectRatio)}
                            className="w-full mt-1 bg-gray-700 border border-gray-600 rounded-lg p-2 text-sm"
                            disabled={isVideoGenerating}
                        >
                            <option value="16:9">16:9 (Landscape)</option>
                            <option value="9:16">9:16 (Portrait)</option>
                        </select>
                    </label>
                </div>
                <button
                    onClick={handleGenerateVideo}
                    disabled={isVideoGenerating || !videoPrompt.trim() || !activeImage}
                    className="w-full text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-500 focus:ring-4 focus:outline-none focus:ring-green-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center transition-colors"
                >
                    {isVideoGenerating ? 'Generating...' : 'Generate Video'}
                </button>
            </div>
          </div>
        </div>

        {/* Right Panel */}
        <div className="w-2/3 flex flex-col items-center justify-center p-8 bg-black/20">
            {error && <div className="absolute top-4 bg-red-800 text-white p-4 rounded-lg shadow-lg">{error}</div>}
          
            {activeImage && !isVideoGenerating && !generatedVideoUrl && (
                <div className="w-full h-full flex items-center justify-center">
                    <img
                        src={`data:${activeImage.mimeType};base64,${activeImage.imageUrl}`}
                        alt={activeImage.prompt}
                        className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                    />
                </div>
            )}
            
            {(isVideoGenerating || generatedVideoUrl) && (
                <div className="w-full h-full flex flex-col items-center justify-center bg-gray-800 rounded-lg p-4">
                    {isVideoGenerating ? (
                        <div className="text-center">
                            <LoadingSpinner />
                            <p className="mt-4 text-lg text-purple-300">Generating Video</p>
                            <p className="text-gray-400">{videoGenerationStatus}</p>
                        </div>
                    ) : (
                        generatedVideoUrl && (
                            <div className="w-full h-full flex flex-col">
                                <video src={generatedVideoUrl} controls autoPlay className="w-full h-full object-contain rounded-lg" />
                                <button
                                    onClick={() => setGeneratedVideoUrl(null)}
                                    className="mt-4 w-full bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
                                >
                                    Back to Image Editor
                                </button>
                            </div>
                        )
                    )}
                </div>
            )}
        </div>
        </div>
      </AuthGuard>
    );
}
